import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Collapse, Stack, styled } from '@mui/material';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IBusinessSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import ServiceGuide, { ServicePreview } from 'src/components/guide/ServiceGuide';
import Iconify from 'src/components/iconify';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IBusinessGridRow } from 'src/hooks/useGetGridCol';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import {
  ContentBox,
  ContentButtonCreate,
  ContentButtonText,
  ContentYelloButton,
} from 'src/layouts/form/FormButton';
import { ButtonContainer } from 'src/layouts/form/FormLayout';
import { IBusiness, useStores } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { PATH_PAGE } from 'src/routes/paths';
import { businessApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { BusinessFormDialog } from './BusinessFormDialog';

interface IBusinessGridProps {
  searchData: Partial<IBusinessSFilter>;
}

export const BusinessGrid = ({ searchData }: IBusinessGridProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { businessStore } = useStores();
  const { getBusinessGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { getBusinessAreas, getBusinessThumbnail } = useGetGuideInfo();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IBusinessGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);
  // 가이드 토글
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // 사업 목록 조회
  const getBusinessData = useCallback(async () => {
    const formData = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await businessApi.getBusiness(formData).then((res) => {
      if (res) {
        const newGridRows = res.data.map((business, i) => {
          return { ...business, id: business.businessSid ?? i };
        });
        setGridRows(newGridRows as IBusinessGridRow[]);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData]);

  useEffect(() => {
    getBusinessData();
  }, [getBusinessData]);

  // 등록 버튼: 등록 dialog open
  const handleAddBusiness = () => {
    openDialog({
      id: FORM_IDS.BUSINESS,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      dialogContent: (
        <BusinessFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IBusinessGridRow>) => {
    callApiWithLoading(async () => {
      await businessApi.getDetail(param.row.businessSid!).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.BUSINESS,
            formType: FormType.UPDATE,
            maxWidth: 'xl',
            dialogContent: (
              <BusinessFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data as IBusiness}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.BUSINESS);
    getBusinessData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    businessStore.updatePagination({
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    });
    getBusinessData();
  };

  // 카테고리 관리 페이지로 이동
  const handleMoveCategory = () => {
    navigate(PATH_PAGE.management.contents.category);
  };

  // 가이드 토글
  const handleGuideToggle = () => {
    setIsGuideOpen(!isGuideOpen);
  };

  return (
    <ContentBox>
      <ButtonContainer>
        <ContentYelloButton onClick={handleMoveCategory}>
          {t('business.button.category')}
        </ContentYelloButton>
        <ButtonGroup>
          <ContentButtonCreate onClick={handleAddBusiness}>
            {t('common.button.register')}
          </ContentButtonCreate>
          <ContentButtonText onClick={handleGuideToggle}>
            <Iconify icon="mdi:help-circle-outline" />
          </ContentButtonText>
        </ButtonGroup>
      </ButtonContainer>
      <Collapse in={isGuideOpen}>
        <ServicePreview>
          <ServiceGuide areas={getBusinessThumbnail()} />
          <ServiceGuide areas={getBusinessAreas()} />
        </ServicePreview>
      </Collapse>
      <CDataGridPro
        columns={getBusinessGridCol()}
        rows={gridRows}
        rowCount={toJS(businessStore.pagination.totalElements)}
        rowHeight={120}
        paginationModel={{
          page: businessStore.pagination.page - 1,
          pageSize: businessStore.pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{ footer: CGridFooter }}
        onRowDoubleClick={handleRowDoubleClick}
      />
    </ContentBox>
  );
};

export default observer(BusinessGrid);

const ButtonGroup = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));
